import { useEffect, useState } from "react";
import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import OrderService from "@app/services/http/order.service";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { IcGamepad, IcMonitor, IcServer } from "@app/components/icons";
import PcOrders from "./pc-orders";
import CloudOrders from "./cloud-orders";
import VpsOrders from "./vps-orders";

type OrderType = "pc" | "cloud" | "vps";

const TYPES: [OrderType, string, typeof IcMonitor][] = [
  ["pc", "Thuê máy tính", IcMonitor],
  ["cloud", "Cloud Gaming - Render", IcGamepad],
  ["vps", "Thuê VPS", IcServer],
];

/** Quản lý đơn hàng: gộp đơn Thuê máy tính, Cloud Gaming - Render và Thuê VPS (loại đơn lưu trên URL ?type=) */
function OrderManagement() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const type: OrderType = (["cloud", "vps"].includes(params.get("type") || "") ? params.get("type") : "pc") as OrderType;
  const [counts, setCounts] = useState<Partial<Record<OrderType, number>>>({});
  // Loại đơn đã mở: giữ lại (chỉ ẩn) để chuyển qua lại không phải tải lại và không bị nháy
  const [visited, setVisited] = useState<OrderType[]>([type]);

  useEffect(() => {
    setVisited((list) => (list.includes(type) ? list : [...list, type]));
  }, [type]);

  const { subscribeOnce } = useObservable();

  useEffect(() => {
    TYPES.forEach(([key]) =>
      subscribeOnce(OrderService.getOrderSummary(key), (res) => setCounts((c) => ({ ...c, [key]: res?.total || 0 })))
    );
  }, []);

  return (
    <div className="flex flex-col gap-5 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">{t("Quản lý đơn hàng")}</h1>
        <p className="m-0 mt-1 max-w-[70ch] text-muted">
          {t("Xem lại mọi đơn thuê máy tính, Cloud Gaming - Render và VPS ở một chỗ. Bấm Lịch sử để xem các lần gia hạn hoặc phiên sử dụng.")}
        </p>
      </div>

      <div role="tablist" aria-label={t("Loại đơn hàng")} className="grid grid-cols-3 gap-2.5 max-[760px]:grid-cols-1 max-[760px]:gap-1.5">
        {TYPES.map(([key, label, Icon]) => {
          const on = type === key;

          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setParams(key === "pc" ? {} : { type: key }, { replace: true })}
              className={clsx(
                "flex min-w-0 items-center gap-2.5 rounded-xl border bg-surface px-3.5 py-3 text-left font-semibold transition-shadow max-[760px]:px-3 max-[760px]:py-2.5",
                on ? "border-brand text-fg shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line text-muted hover:border-line-2"
              )}
            >
              <Icon className={clsx("h-5 w-5 flex-none", on && "text-brand")} />
              <span className="min-w-0 flex-1">{t(label)}</span>
              {counts[key] !== undefined && (
                <em
                  className={clsx(
                    "rounded-full px-2 py-px text-xs not-italic tabular-nums",
                    on ? "bg-brand text-brand-ink" : "bg-surface-2 text-muted"
                  )}
                >
                  {counts[key]}
                </em>
              )}
            </button>
          );
        })}
      </div>

      {visited.includes("pc") && (
        <div className={type === "pc" ? "flex flex-col gap-5" : "hidden"}>
          <PcOrders />
        </div>
      )}
      {visited.includes("cloud") && (
        <div className={type === "cloud" ? "flex flex-col gap-5" : "hidden"}>
          <CloudOrders />
        </div>
      )}
      {visited.includes("vps") && (
        <div className={type === "vps" ? "flex flex-col gap-5" : "hidden"}>
          <VpsOrders />
        </div>
      )}
    </div>
  );
}

export default OrderManagement;
